import React from 'react';
import styled from 'styled-components';
import moment from 'moment';
import { ColumnsType } from 'antd/es/table';
import { Modal, Table, Text } from 'components';
import { capitalize, formatDocumentType } from 'utils';
import DocumentsContext from './DocumentsContext';
import { DocumentType } from './types';

const formatAction = (action: string) => {
  if (action === 'type_changed') return 'Type Changed';
  return capitalize(action);
};

const tablePadding = 25;

const columns: ColumnsType<DocumentType['history']> = [
  {
    title: 'Status',
    dataIndex: 'action',
    key: 'action',
    width: 150,
    onHeaderCell: () => ({
      style: { paddingLeft: tablePadding },
    }),
    onCell: () => ({
      style: { paddingLeft: tablePadding },
    }),
    render: action => <Text nowrap>{formatAction(action)}</Text>,
  },
  {
    title: 'User',
    dataIndex: 'userName',
    key: 'userName',
    width: 150,
    render: userName => <Text nowrap>{userName}</Text>,
  },
  {
    title: 'Date',
    dataIndex: 'date',
    key: 'date',
    width: 150,
    onHeaderCell: () => ({
      style: { paddingRight: tablePadding },
    }),
    onCell: () => ({
      style: { paddingRight: tablePadding },
    }),
    render: date => (
      <Text nowrap>{moment(date).format('h:mm A, MMM D YYYY')}</Text>
    ),
  },
];

const DocumentHistory: React.FC = () => {
  const { historyOpen, setHistoryOpen, documents } =
    React.useContext(DocumentsContext);

  const { history } = documents?.find(({ id }) => id === historyOpen) || {};

  const expandedRowKeys = history
    ?.filter(h => ['rejected', 'type_changed', 'renamed'].includes(h.action))
    .map(h => h.date.toString());

  if (!history) return null;

  return (
    <Modal
      title="History"
      open={Boolean(historyOpen)}
      onCancel={() => setHistoryOpen(null)}
      footer={null}
      width={700}
      bodyStyle={{ paddingTop: 0, paddingLeft: 0, paddingRight: 0 }}
    >
      <StyledTable
        type="primary"
        dataSource={history
          .map(h => ({
            ...h,
            key: h.date.toString(),
          }))
          .sort((a, b) => {
            if (moment(a.date).isBefore(moment(b.date))) {
              return 1;
            }
            return -1;
          })}
        columns={columns}
        pagination={false}
        style={{ overflow: 'auto' }}
        scroll={{
          x: 'auto',
          y: '50vh',
        }}
        onRow={({ key }) => ({
          className: expandedRowKeys.includes(key) && 'expanded-row',
        })}
        expandable={{
          expandedRowKeys,
          expandedRowRender: ({
            action,
            reason,
            typeFrom,
            typeTo,
            nameFrom,
            nameTo,
          }) => {
            if (action === 'rejected') {
              return <Text type="secondary">&quot;{reason}&quot;</Text>;
            }
            if (action === 'renamed') {
              return (
                <Text type="secondary">
                  From &quot;{nameFrom}&quot; to &quot;
                  {nameTo}&quot;
                </Text>
              );
            }
            return (
              <Text type="secondary">
                From &quot;{formatDocumentType(typeFrom)}&quot; to &quot;
                {formatDocumentType(typeTo)}&quot;
              </Text>
            );
          },
          expandIconColumnIndex: -1,
          expandedRowClassName: () => 'expansion',
        }}
      />
    </Modal>
  );
};

const StyledTable = styled(Table)`
  .expanded-row {
    td {
      border-bottom: none;
    }
  }
  .expansion {
    td {
      background: transparent;
      padding: 0 ${tablePadding}px;
      padding-bottom: 20px;
    }
  }
`;

export default DocumentHistory;
